import { LowerCasePipe } from '@angular/common';
import { Component, computed, effect } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { DescriptionComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/description/description.component';
import { IKpiCard } from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { ITextPostAnalyticsTableData } from '@cta-analytics-dialog/deliverable-analytics-content/analytics-details-table/analytics-details-table.interface';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { EDeliverableType } from '@enums/campaign.enum';
import { IDescriptionAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { TextPostAnalyticsBaseDirective } from '../text-post-analytics-base/text-post-analytics-base.directive';

@Component({
  imports: [TranslateModule, MatButtonModule, DescriptionComponent, LowerCasePipe],
  providers: [DeliverableFindingsStore],
  selector: 'app-description-post-analytics',
  templateUrl: './description-post-analytics.component.html',
})
export class DescriptionPostAnalyticsComponent extends TextPostAnalyticsBaseDirective<
  ITextPostAnalyticsTableData,
  IDescriptionAnalytics
> {
  public override readonly kpiCardData: IKpiCard = {
    icon: 'view.svg',
    id: 'description-count',
    title: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.talkingPoint.totalCount'),
    value: 0,
  };
  public override readonly deliverableType = EDeliverableType.description;

  public override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.descriptions?.find((c) => c.deliverableId === deliverableId);
    }

    return null;
  });

  public override readonly contentData = computed(() => {
    const socialContent = this.executionAnalyticsWithFindings()?.content?.socialContent;

    if (!socialContent) {
      return null;
    }

    if ('youtubeData' in socialContent) {
      return socialContent.youtubeData;
    }

    return null;
  });

  constructor() {
    super();
  }

  public override highlightKeywords(text: string): string {
    const normalized = AnalyticsDetailsUtilitiesService.normalizeWhitespace(text);

    return AnalyticsDetailsUtilitiesService.highlightKeywords(this.keywords, normalized);
  }

  protected override setKeywordCounts(): void {
    const description = this.currentDeliverable();

    const keywords = description?.keywords?.map((k) => k.keyword) ?? [];
    const descriptionText = AnalyticsDetailsUtilitiesService.normalizeWhitespace(this.contentData()?.description ?? '');
    this.keywordCounts = AnalyticsDetailsUtilitiesService.getIndividualKeywordCounts(keywords, [descriptionText]);

    const total = Object.values(this.keywordCounts).reduce((sum, val) => sum + val, 0);
    this.kpiCardData.value = total;

    AnalyticsDetailsUtilitiesService.mapKeywordCountsToDeliverableForm(this.deliverableForm(), this.keywordCounts);
  }

  protected override setKeywordCardList(): void {
    const description = this.currentDeliverable();

    if (description?.keywords && description.keywords.length) {
      const kpiCardList = description.keywords.map((k) => {
        return {
          icon: 'keyword.svg',
          id: `${k.keyword}-kpi-card`,
          keyword: k.keyword,
          value: this.keywordCounts[k.keyword] ?? 0,
        };
      });

      this.keywordCardList.set(kpiCardList);
    }
  }

  protected override setKeywordsEffect(): void {
    effect(() => {
      const deliverableId = this.deliverableId();
      const executionAnalytics = this.executionAnalyticsWithFindings();

      if (deliverableId && executionAnalytics) {
        this.keywords = this.currentDeliverable()?.keywords?.map((k) => k.keyword) ?? [];
      }
    });
  }
}
